"use client";

import { useState, useCallback, useEffect } from "react";
import { Quiz, QuizQuestion } from "@/lib/quizGenerator";

interface QuizViewProps {
  quiz: Quiz | null;
  onGenerateQuiz: () => void;
  isGenerating: boolean;
  hasArticle: boolean;
  hasHistory: boolean;
  timeRemaining: number;
  isTimerActive: boolean;
  quizArticleTitle?: string;
  onAnswerCountChange?: (count: number) => void;
}

function QuestionCard({
  question,
  questionNumber,
  onAnswer,
  userAnswer,
  showResult,
}: {
  question: QuizQuestion;
  questionNumber: number;
  onAnswer: (answer: string | boolean) => void;
  userAnswer: string | boolean | null;
  showResult: boolean;
}) {
  const isCorrect = showResult && userAnswer === question.correctAnswer;
  const isWrong =
    showResult && userAnswer !== null && userAnswer !== question.correctAnswer;

  return (
    <div
      className={`p-4 rounded-xl border-2 transition-all duration-300 ${
        isCorrect
          ? "bg-green-50 border-green-400"
          : isWrong
            ? "bg-red-50 border-red-400"
            : "bg-white border-gray-200"
      }`}
    >
      <div className="flex items-start gap-3 mb-3">
        <span
          className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
            isCorrect
              ? "bg-green-500 text-white"
              : isWrong
                ? "bg-red-500 text-white"
                : "bg-[#141beb] text-white"
          }`}
        >
          {questionNumber}
        </span>
        <div className="flex-1">
          <span
            className={`inline-block px-2 py-0.5 text-xs rounded-full mb-2 ${
              question.type === "fill-blank"
                ? "bg-purple-100 text-purple-700"
                : question.type === "true-false"
                  ? "bg-blue-100 text-blue-700"
                  : "bg-orange-100 text-orange-700"
            }`}
          >
            {question.type === "fill-blank"
              ? "Fill in the Blank"
              : question.type === "true-false"
                ? "True or False"
                : "Multiple Choice"}
          </span>
          <p className="text-gray-800 font-medium">{question.question}</p>
        </div>
      </div>

      {/* Answer Options */}
      <div className="ml-10 space-y-2">
        {question.type === "fill-blank" ? (
          <input
            type="text"
            placeholder="Type your answer..."
            className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:border-[#141beb] ${
              showResult
                ? isCorrect
                  ? "border-green-400 bg-green-50"
                  : "border-red-400 bg-red-50"
                : "border-gray-200"
            }`}
            value={typeof userAnswer === "string" ? userAnswer : ""}
            onChange={(e) => onAnswer(e.target.value)}
            disabled={showResult}
          />
        ) : question.type === "true-false" ? (
          <div className="flex gap-2">
            {["True", "False"].map((opt) => {
              const isSelected = userAnswer === (opt === "True");
              const optValue = opt === "True";
              const isThisCorrect = question.correctAnswer === optValue;

              return (
                <button
                  key={opt}
                  onClick={() => onAnswer(optValue)}
                  disabled={showResult}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                    showResult
                      ? isThisCorrect
                        ? "bg-green-500 text-white"
                        : isSelected
                          ? "bg-red-500 text-white"
                          : "bg-gray-100 text-gray-500"
                      : isSelected
                        ? "bg-[#141beb] text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {opt}
                </button>
              );
            })}
          </div>
        ) : (
          <div className="grid gap-2">
            {question.options?.map((opt, idx) => {
              const isSelected = userAnswer === opt;
              const isThisCorrect = question.correctAnswer === opt;

              return (
                <button
                  key={idx}
                  onClick={() => onAnswer(opt)}
                  disabled={showResult}
                  className={`text-left py-2 px-4 rounded-lg font-medium transition-all ${
                    showResult
                      ? isThisCorrect
                        ? "bg-green-500 text-white"
                        : isSelected
                          ? "bg-red-500 text-white"
                          : "bg-gray-100 text-gray-500"
                      : isSelected
                        ? "bg-[#141beb] text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  <span className="mr-2">{String.fromCharCode(65 + idx)}.</span>
                  {opt}
                </button>
              );
            })}
          </div>
        )}

        {/* Explanation */}
        {showResult && (
          <div
            className={`mt-3 p-3 rounded-lg text-sm ${
              isCorrect
                ? "bg-green-100 text-green-800"
                : "bg-amber-100 text-amber-800"
            }`}
          >
            <span className="font-semibold">
              {isCorrect ? "✓ Correct!" : "✗ Not quite."}
            </span>{" "}
            {question.explanation}
          </div>
        )}
      </div>
    </div>
  );
}

export function QuizView({
  quiz,
  onGenerateQuiz,
  isGenerating,
  hasArticle,
  hasHistory,
  timeRemaining,
  isTimerActive,
  quizArticleTitle,
  onAnswerCountChange,
}: QuizViewProps) {
  const [answers, setAnswers] = useState<Record<number, string | boolean>>({});
  const [showResults, setShowResults] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Reset state when quiz changes
  useEffect(() => {
    setAnswers({});
    setShowResults(false);
    setSubmitted(false);
    onAnswerCountChange?.(0);
  }, [quiz, onAnswerCountChange]);

  // Format time remaining as MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const handleAnswer = useCallback(
    (questionId: number, answer: string | boolean) => {
      if (!submitted) {
        setAnswers((prev) => {
          const newAnswers = { ...prev, [questionId]: answer };
          onAnswerCountChange?.(Object.keys(newAnswers).length);
          return newAnswers;
        });
      }
    },
    [submitted, onAnswerCountChange],
  );

  const handleSubmit = useCallback(() => {
    setSubmitted(true);
    setShowResults(true);
  }, []);

  const handleRetry = useCallback(() => {
    setAnswers({});
    setShowResults(false);
    setSubmitted(false);
  }, []);

  const handleNewQuiz = useCallback(() => {
    setAnswers({});
    setShowResults(false);
    setSubmitted(false);
    onGenerateQuiz();
  }, [onGenerateQuiz]);

  // Calculate score
  const score = quiz
    ? quiz.questions.reduce((acc, q) => {
        const userAnswer = answers[q.id];
        if (q.type === "fill-blank") {
          // Case-insensitive comparison for fill-blank
          return (
            acc +
            (String(userAnswer).toLowerCase().trim() ===
            String(q.correctAnswer).toLowerCase().trim()
              ? 1
              : 0)
          );
        }
        return acc + (userAnswer === q.correctAnswer ? 1 : 0);
      }, 0)
    : 0;

  const answeredCount = Object.keys(answers).length;
  const totalQuestions = quiz?.questions.length ?? 0;
  const isTimeUp = timeRemaining <= 0 && isTimerActive;

  // Auto-submit when time runs out - MUST be before any returns
  useEffect(() => {
    if (isTimeUp && !submitted && quiz) {
      setSubmitted(true);
      setShowResults(true);
    }
  }, [isTimeUp, submitted, quiz]);

  // Early returns AFTER all hooks
  if (!hasHistory) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-gray-500 py-12">
        <div className="w-16 h-16 mb-4 rounded-full bg-gray-100 flex items-center justify-center">
          <span className="text-3xl">📝</span>
        </div>
        <p className="text-lg font-medium">No reading history yet</p>
        <p className="text-sm mt-2 text-center">
          Read some articles first, then come back to test your knowledge!
        </p>
      </div>
    );
  }

  if (!quiz) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center py-12">
        <div className="w-20 h-20 mb-6 rounded-full bg-gradient-to-br from-[#141beb]/10 to-[#02eec4]/10 flex items-center justify-center">
          {isGenerating ? (
            <span className="text-4xl animate-spin">⏳</span>
          ) : (
            <span className="text-4xl">🧠</span>
          )}
        </div>
        <h2 className="text-xl font-bold text-gray-800 mb-2">
          {isGenerating ? "Generating Quiz..." : "Preparing Quiz"}
        </h2>
        <p className="text-sm text-gray-500 text-center mb-6 max-w-sm">
          {isGenerating
            ? `Creating questions from: ${quizArticleTitle || "random article"}...`
            : "A random article from your history will be selected for the quiz!"}
        </p>
        {!isGenerating && (
          <div className="animate-pulse text-gray-400 text-sm">Loading...</div>
        )}
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col">
      {/* Timer Display */}
      {isTimerActive && !showResults && (
        <div className="mb-4 flex justify-center">
          <div
            className={`px-6 py-3 rounded-xl font-mono text-2xl font-bold ${
              timeRemaining <= 30 ? "animate-pulse" : ""
            }`}
            style={{
              backgroundColor: "#000000",
              color: timeRemaining <= 30 ? "#ff4444" : "#00ff00",
              boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
            }}
          >
            ⏱️ {formatTime(timeRemaining)}
          </div>
        </div>
      )}

      {/* Time Up Message */}
      {isTimeUp && (
        <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded-xl text-center">
          <span className="text-red-600 font-bold">⏰ Time&apos;s up!</span>
        </div>
      )}

      {/* Quiz Header */}
      <div
        className="mb-4 p-4 rounded-xl"
        style={{ background: "linear-gradient(to right, #e8efff, #e5f4ff)" }}
      >
        <h2 className="text-lg font-bold text-gray-800 mb-1">
          Quiz: {quiz.articleTitle}
        </h2>
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">
            {totalQuestions} question{totalQuestions !== 1 ? "s" : ""}
          </span>
          {showResults && (
            <span
              className={`font-bold ${score === totalQuestions ? "text-green-600" : score >= totalQuestions / 2 ? "text-[#141beb]" : "text-orange-600"}`}
            >
              Score: {score}/{totalQuestions} (
              {Math.round((score / totalQuestions) * 100)}%)
            </span>
          )}
        </div>
      </div>

      {/* Questions */}
      <div className="flex-1 overflow-y-auto space-y-4 mb-4">
        {quiz.questions.map((question, idx) => (
          <QuestionCard
            key={question.id}
            question={question}
            questionNumber={idx + 1}
            onAnswer={(answer) => handleAnswer(question.id, answer)}
            userAnswer={answers[question.id] ?? null}
            showResult={showResults}
          />
        ))}
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4 border-t border-gray-200">
        {!showResults ? (
          <button
            onClick={handleSubmit}
            disabled={answeredCount < totalQuestions}
            className="flex-1 py-3 bg-gradient-to-r from-[#141beb] to-[#02eec4] text-white font-bold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            Submit Answers ({answeredCount}/{totalQuestions})
          </button>
        ) : (
          <>
            <button
              onClick={handleRetry}
              className="flex-1 py-3 bg-white text-[#141beb] font-bold rounded-xl border-2 border-[#141beb] hover:bg-[#141beb]/5 transition-all"
            >
              Try Again
            </button>
            <button
              onClick={handleNewQuiz}
              className="flex-1 py-3 bg-gradient-to-r from-[#141beb] to-[#02eec4] text-white font-bold rounded-xl transition-all"
            >
              New Quiz
            </button>
          </>
        )}
      </div>
    </div>
  );
}
